import React, { useRef, useEffect, useState } from 'react';
import * as faceapi from 'face-api.js';
import * as tf from '@tensorflow/tfjs';

function FaceCapture({ onCapture, mode = 'register' }) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [modelsLoaded, setModelsLoaded] = useState(false);
  const [capturing, setCapturing] = useState(false);
  const [faceDetected, setFaceDetected] = useState(false);

  useEffect(() => {
    loadModels();
    return () => {
      stopCamera();
    };
  }, []);

  const loadModels = async () => {
    try {
      // Try WebGL first, fall back to CPU if unavailable
      try {
        await tf.setBackend('webgl');
        await tf.ready();
        console.log('FaceCapture: Using WebGL backend');
      } catch (webglError) {
        console.warn('FaceCapture: WebGL not available, falling back to CPU backend');
        await tf.setBackend('cpu');
        await tf.ready();
        console.log('FaceCapture: Using CPU backend');
      }

      const MODEL_URL = `${process.env.REACT_APP_BACKEND_URL}/api/models`;
      await Promise.all([
        faceapi.nets.tinyFaceDetector.loadFromUri(MODEL_URL),
        faceapi.nets.faceLandmark68Net.loadFromUri(MODEL_URL),
        faceapi.nets.faceRecognitionNet.loadFromUri(MODEL_URL),
      ]);
      setModelsLoaded(true);
      startCamera();
    } catch (error) {
      console.error('Error loading models:', error);
    }
  };

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user', width: { ideal: 640 }, height: { ideal: 480 } },
      });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.onloadedmetadata = () => {
          videoRef.current.play().catch(e => console.warn('Auto-play blocked:', e));
        };
      }
    } catch (error) {
      console.error('Error accessing camera:', error);
    }
  };

  const stopCamera = () => {
    if (videoRef.current && videoRef.current.srcObject) {
      const tracks = videoRef.current.srcObject.getTracks();
      tracks.forEach((track) => track.stop());
    }
  };

  const detectFace = async () => {
    if (!videoRef.current || !modelsLoaded) return;

    const detections = await faceapi
      .detectSingleFace(videoRef.current, new faceapi.TinyFaceDetectorOptions())
      .withFaceLandmarks()
      .withFaceDescriptor();

    if (detections) {
      setFaceDetected(true);
      
      if (canvasRef.current) {
        const canvas = canvasRef.current;
        const displaySize = { width: videoRef.current.width, height: videoRef.current.height };
        faceapi.matchDimensions(canvas, displaySize);
        const resizedDetections = faceapi.resizeResults(detections, displaySize);
        canvas.getContext('2d').clearRect(0, 0, canvas.width, canvas.height);
        faceapi.draw.drawDetections(canvas, resizedDetections);
      }
    } else {
      setFaceDetected(false);
      if (canvasRef.current) {
        canvasRef.current.getContext('2d').clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
      }
    }

    return detections;
  };

  useEffect(() => {
    if (modelsLoaded && videoRef.current) {
      const interval = setInterval(() => {
        detectFace();
      }, 100);
      return () => clearInterval(interval);
    }
  }, [modelsLoaded]);

  const capturePhoto = async () => {
    setCapturing(true);
    const detection = await detectFace();

    if (detection) {
      const canvas = document.createElement('canvas');
      canvas.width = videoRef.current.videoWidth;
      canvas.height = videoRef.current.videoHeight;
      canvas.getContext('2d').drawImage(videoRef.current, 0, 0);
      const photoDataUrl = canvas.toDataURL('image/jpeg');

      onCapture({
        descriptor: Array.from(detection.descriptor),
        photo: photoDataUrl,
      });
    } else {
      alert('No face detected. Please ensure your face is clearly visible.');
    }
    setCapturing(false);
  };

  return (
    <div className="space-y-4">
      <div className="relative bg-black rounded-lg overflow-hidden" style={{ aspectRatio: '4/3' }}>
        {!modelsLoaded && (
          <div className="absolute inset-0 flex items-center justify-center bg-slate-950">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
              <p className="text-slate-400">Loading face detection models...</p>
            </div>
          </div>
        )}
        
        <video
          ref={videoRef}
          autoPlay
          muted
          playsInline
          width="640"
          height="480"
          className="w-full h-full object-cover"
          data-testid="face-capture-video"
        />
        
        <canvas
          ref={canvasRef}
          className="absolute top-0 left-0 w-full h-full"
        />
        
        {modelsLoaded && (
          <>
            <div className="scanner-beam" />
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="face-guide w-64 h-64" />
            </div>
          </>
        )}
        
        {faceDetected && (
          <div className="absolute top-4 left-4 bg-emerald-600 text-white px-3 py-1 rounded-md text-sm font-medium" data-testid="face-detected-indicator">
            Face Detected ✓
          </div>
        )}
      </div>

      <button
        onClick={capturePhoto}
        disabled={!modelsLoaded || capturing || !faceDetected}
        data-testid="capture-face-button"
        className="w-full bg-blue-600 hover:bg-blue-500 text-white font-medium px-6 py-3 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {capturing ? 'Capturing...' : mode === 'register' ? 'Capture Face' : 'Recognize & Mark Attendance'}
      </button>
    </div>
  );
}

export default FaceCapture;
